import type { DeliveryByEvent, Price } from '@noths/polaris-dev-ts-types';

/**
 * How many images a product carries.
 *
 * The listings API puts no bound on the array, so an unusually well-photographed
 * product would otherwise serialise dozens of URLs into every page that lists it.
 * The card caps what it renders at the same number, so anything past this is paid
 * for on the wire and in the adapter and then discarded at render.
 *
 * It mirrors the card's own cap rather than importing it, which the design system
 * does not re-export. `ProductList.spec.tsx` holds the two to each other by rendering
 * a card rather than by comparing constants, since what matters is how many images
 * the card draws.
 */
export const PRODUCT_IMAGE_LIMIT = 5;

export interface ProductPartner {
  name: string;
  shortcode: string;
}

export interface ProductDelivery {
  /**
   * In major units, as the listings API gives it: 3.99 means £3.99. Note that
   * `price` and `preSalePrices` are in minor units, so anything reading both has
   * to convert.
   *
   * Zero is a real value meaning free delivery, not a missing one.
   */
  cost: number;
  /** Lead time in whole days from the request, which a consumer turns into a date. */
  days: number;
}

export interface Product {
  available: boolean;
  /** Average customer rating, 0 to 5. Absent when the API returns no rating. */
  averageRating?: number;
  code: number;
  /**
   * The one delivery option quoted on this product, or `null` when there is none
   * to quote.
   *
   * Held as a single object rather than as two nullable fields so that a cost can
   * never be paired with a lead time belonging to a different option. Consumers
   * get both halves or neither, which is what lets a card promise that its date
   * and its price describe the same thing.
   */
  delivery: ProductDelivery | null;
  deliveryByEvent?: Omit<DeliveryByEvent, 'lastDeliveryBy'> | null;
  detailPageHref: string;
  /**
   * Set only when the product carries the `highly_rated` attribute. Absent rather
   * than `false` so an unremarkable product costs nothing in the serialised page.
   */
  highlyRated?: boolean;
  /**
   * Ordered image URLs, primary first, at most `PRODUCT_IMAGE_LIMIT` of them. Each
   * retains its `{size}` CDN token, so every consumer resolves the size it needs
   * with `getProductImageURL`.
   */
  images: string[];
  isExpandedSearch?: boolean;
  isNew: boolean;
  onSale: boolean;
  partner: ProductPartner;
  preSalePrices: Price[] | null;
  price: Price;
  purchasable: boolean;
  salePercentage: number | null;
  /**
   * Set only when the product carries the `star_brand` attribute. Absent rather than
   * `false`, for the same reason as `highlyRated`.
   *
   * `star_brand` is the only name the catalogue has for this; there is no
   * `top_brand` attribute. It describes the **partner**, not the product, which is
   * why a listing scoped to one partner suppresses the badge it drives.
   */
  starBrand?: boolean;
  title: string;
}
